/**
 * 176. View as — the part with no Supabase import, so lib/supabase.ts can use
 * it to build the client without a cycle. The doors are in lib/viewAs.ts.
 *
 * ── WHAT THE BROWSER HOLDS ─────────────────────────────────────────────────
 *
 * One session id, in sessionStorage: this tab only, gone when the tab closes,
 * so a second tab is yourself unless you start it there too. The SERVER is
 * the record (view_as_session) and the only judge — a stale id here is
 * refused by view_as_pre_request() with `view_as_ended`, and viewAsFetch then
 * forgets it and reloads as yourself.
 *
 * ── WHICH REQUESTS CARRY IT ────────────────────────────────────────────────
 *
 * PostgREST (/rest/v1) only, and never the three view-as doors themselves:
 * they act for the administrator, and a request carrying the marker is
 * read-only, so start and end would fail. Auth, storage, realtime and edge
 * functions never carry it — images load as yourself and Ask One answers as
 * yourself, which is the honest failure (see migration 176's header).
 *
 * IT IS NEVER A PERMISSION. Deleting this file's state cannot see as anyone;
 * forging it gets `view_as_ended`. The read-only guarantee is the database's.
 */
export type ViewAs = {
  sessionId: string
  personId: string
  displayName: string
  /** ISO timestamp; the server ends the session then, whatever this says. */
  expiresAt: string
}

const KEY = 'oneconetec.viewAs.v1'

/** The view-as doors: they act for the administrator and must not carry it. */
const OWN_DOORS = ['start_view_as', 'end_view_as', 'my_view_as']

function read(): ViewAs | null {
  try {
    const raw = sessionStorage.getItem(KEY)
    if (!raw) return null
    const v = JSON.parse(raw) as Partial<ViewAs>
    return v && typeof v.sessionId === 'string' && typeof v.personId === 'string'
      ? { sessionId: v.sessionId, personId: v.personId, displayName: v.displayName ?? '', expiresAt: v.expiresAt ?? '' }
      : null
  } catch {
    return null
  }
}

let current: ViewAs | null = typeof window === 'undefined' ? null : read()

/** Who this tab is viewing as, or null. Presentation only. */
export function viewingAs(): ViewAs | null {
  return current
}

export function setViewingAs(v: ViewAs | null): void {
  current = v
  try {
    if (v) sessionStorage.setItem(KEY, JSON.stringify(v))
    else sessionStorage.removeItem(KEY)
  } catch {
    // Private window, blocked storage: the session still works until reload.
  }
}

/** Does a request to this URL carry the marker? Pure, for the suite. */
export function carriesViewAs(url: string): boolean {
  const at = url.indexOf('/rest/v1/')
  if (at < 0) return false
  const rest = url.slice(at + '/rest/v1/'.length)
  return !OWN_DOORS.some((d) => rest === `rpc/${d}` || rest.startsWith(`rpc/${d}?`))
}

/** The server's refusal of a session that ended, expired or was revoked. */
export function isViewAsEnded(body: unknown): boolean {
  return !!body && typeof body === 'object' && (body as { message?: unknown }).message === 'view_as_ended'
}

/** 25006: what every write answers while viewing as somebody. */
export function isReadOnlyRefusal(e: unknown): boolean {
  return !!e && typeof e === 'object' && (e as { code?: unknown }).code === '25006'
}

/**
 * Wraps fetch for supabase-js. Adds `x-view-as` where carriesViewAs says so;
 * on `view_as_ended` forgets the session and reloads as yourself, once — the
 * reload has nothing to send, so it cannot loop.
 */
export function viewAsFetch(base: typeof fetch): typeof fetch {
  return async (input: RequestInfo | URL, init?: RequestInit) => {
    const v = current
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url
    if (!v || !carriesViewAs(url)) return base(input, init)
    const headers = new Headers(init?.headers ?? (input instanceof Request ? input.headers : undefined))
    headers.set('x-view-as', v.sessionId)
    const res = await base(input, { ...init, headers })
    if (res.status === 400) {
      try {
        if (isViewAsEnded(await res.clone().json())) {
          setViewingAs(null)
          window.location.reload()
        }
      } catch {
        // Not JSON: an ordinary 400, the caller's to report.
      }
    }
    return res
  }
}

/* ── WHAT THE VIEWED PERSON "DID" IN THIS SESSION (David, 12 Oct) ──────────
 *
 * Closing the welcome, putting a notice away and finishing the tour are all
 * writes, and every write is refused while viewing as somebody. So each of
 * them came straight back on the next page — the welcome on every screen.
 * They are remembered HERE instead, for this View as session only (keyed by
 * its id, so the next session starts as the person really is), and the
 * readers filter by it. Nothing is written for them; this is the browser's
 * memory of the preview, not theirs. */
const LOCAL_KEY = 'oneconetec.viewAs.local.v1'
type Local = { sessionId: string; dismissed: string[]; tourSeen: boolean; starOnePreview: boolean }

function readLocal(): Local | null {
  const v = current
  if (!v) return null
  try {
    const raw = sessionStorage.getItem(LOCAL_KEY)
    const l = raw ? (JSON.parse(raw) as Local) : null
    if (l && l.sessionId === v.sessionId) return l
  } catch {
    // fall through to a fresh one
  }
  return { sessionId: v.sessionId, dismissed: [], tourSeen: false, starOnePreview: false }
}

function writeLocal(l: Local): void {
  try {
    sessionStorage.setItem(LOCAL_KEY, JSON.stringify(l))
  } catch {
    // In memory only, then: still right until the next reload.
  }
  memo = l
}
let memo: Local | null = null
function local(): Local | null {
  if (!current) return null
  if (memo && memo.sessionId === current.sessionId) return memo
  memo = readLocal()
  return memo
}

/** Notices put away while viewing as somebody (welcome, strip). */
export function viewAsDismissed(): ReadonlySet<string> {
  return new Set(local()?.dismissed ?? [])
}
export function rememberViewAsDismissed(contentId: string): void {
  const l = local()
  if (l && !l.dismissed.includes(contentId)) writeLocal({ ...l, dismissed: [...l.dismissed, contentId] })
}
/** The tour, finished or skipped while viewing as somebody. */
export function viewAsTourSeen(): boolean {
  return local()?.tourSeen ?? false
}
export function rememberViewAsTourSeen(): void {
  const l = local()
  if (l && !l.tourSeen) writeLocal({ ...l, tourSeen: true })
}
/** Past the Star One agreement for the preview — never joined for them. */
export function viewAsStarOnePreview(): boolean {
  return local()?.starOnePreview ?? false
}
export function rememberViewAsStarOnePreview(): void {
  const l = local()
  if (l && !l.starOnePreview) writeLocal({ ...l, starOnePreview: true })
}
